import { localDay, timezoneOptions } from "@/lib/calendars";

/**
 * When an appointment is booked or moved to (ACL-521): the timezone the booker reads and types
 * times in, and either one of the calendar's free slots or a custom start and end (ADR-169 in
 * `platform`). Every time is held as an instant, so changing the zone changes how a time reads,
 * never which time it is. The zone is the console's alone: core-api takes and answers UTC
 * (ADR-085). Pure, for `test/booking-time.test.ts`.
 */

const MINUTE = 60_000;
const DAY = 24 * 60 * MINUTE;

/** The longest custom time core-api takes (`MAX_CUSTOM_MINUTES` in `schemas.py`). */
export const MAX_CUSTOM_MINUTES = 24 * 60;

/** This browser's zone, or null where Intl cannot say. */
export function browserZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/** Whether Intl knows `zone`: a contact's zone is free text in old records. */
export function isZone(zone: string | null | undefined): zone is string {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** An instant on `zone`'s wall clock, "2026-10-05T09:00": what a `datetime-local` input holds. */
export function wallTime(at: string | number, zone: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    })
      .formatToParts(new Date(at))
      .map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
}

/** How many minutes `zone` is ahead of UTC at an instant: -300 for Chicago in summer. */
export function zoneOffset(zone: string, at: number): number {
  const minute = Math.floor(at / MINUTE) * MINUTE;
  return Math.round((Date.parse(`${wallTime(minute, zone)}:00Z`) - minute) / MINUTE);
}

/**
 * The instant a wall time on `zone`'s clock names, as ISO; null for what is not one. A time the
 * clocks skip (2:30 the night they spring forward) reads as the hour after, as a browser reads it;
 * a time they pass twice (1:30 the night they fall back) as the first.
 */
export function instantOf(wall: string, zone: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/.exec(wall);
  if (!m) return null;
  const minutes = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`;
  const asUtc = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  if (Number.isNaN(asUtc)) return null;
  // The zone's offset a day either side covers any change of clocks that night.
  const before = zoneOffset(zone, asUtc - DAY);
  const after = zoneOffset(zone, asUtc + DAY);
  const fits = [...new Set([before, after])]
    .map((offset) => asUtc - offset * MINUTE)
    .filter((t) => wallTime(t, zone) === minutes);
  return new Date(fits.length ? Math.min(...fits) : asUtc - before * MINUTE).toISOString();
}

/** "GMT-05:00": `zone`'s offset at an instant. */
export function gmtOffset(zone: string, at: string | number = Date.now()): string {
  const offset = zoneOffset(zone, typeof at === "string" ? Date.parse(at) : at);
  const abs = Math.abs(offset);
  return `GMT${offset < 0 ? "-" : "+"}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

/** "CDT"; null where the locale names the zone only by its offset ("GMT+5"). */
export function zoneAbbr(zone: string, at: string | number = Date.now(), locale = "en-US"): string | null {
  const name = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: "short" })
    .formatToParts(new Date(at))
    .find((p) => p.type === "timeZoneName")?.value;
  return name && !/^(GMT|UTC)/.test(name) ? name : null;
}

/** "GMT-05:00 America/Chicago (CDT)", as GoHighLevel's picker reads. */
export function zoneLabel(zone: string, at: string | number = Date.now(), locale = "en-US"): string {
  const abbr = zoneAbbr(zone, at, locale);
  return `${gmtOffset(zone, at)} ${zone.replace(/_/g, " ")}${abbr ? ` (${abbr})` : ""}`;
}

/** A zone the picker offers, with whose clock it is: "Calendar", "Your computer", "Contact". */
export interface ZoneChoice {
  zone: string;
  whose: string[];
}

/** The zones the booking picker offers, each once: the calendar's, this computer's, the contact's,
 *  then the US zones. */
export function zoneChoices(calendarZone: string, own: string | null, contactZone?: string | null): ZoneChoice[] {
  const out: ZoneChoice[] = [];
  const add = (zone: string | null | undefined, whose?: string) => {
    if (!isZone(zone)) return;
    const found = out.find((c) => c.zone === zone);
    if (!found) out.push({ zone, whose: whose ? [whose] : [] });
    else if (whose) found.whose.push(whose);
  };
  add(calendarZone, "Calendar");
  add(own, "Your computer");
  add(contactZone, "Contact");
  for (const zone of timezoneOptions()) add(zone);
  return out;
}

export type TimeMode = "default" | "custom";

/** A time being picked: the zone it reads in, and the free slot or the custom start and end. */
export interface When {
  /** The zone the times are shown and typed in. */
  zone: string;
  /** The calendar's: where `zone` starts, and what it follows to another calendar until changed. */
  calendarZone: string;
  mode: TimeMode;
  /** The slot picker's day, on `zone`'s clock: "2026-10-05". */
  day: string | null;
  /** The free slot picked. */
  slot: string | null;
  /** The custom start and end. */
  from: string | null;
  to: string | null;
}

/** Where the picker opens: on the calendar's clock, on a day, and with a custom time to start from. */
export function openWhen(
  calendarZone: string,
  at: { day?: string | null; from?: string | null; to?: string | null } = {},
): When {
  return {
    zone: calendarZone,
    calendarZone,
    mode: "default",
    day: at.day ?? null,
    slot: null,
    from: at.from ?? null,
    to: at.to ?? null,
  };
}

/** Another calendar: its clock, unless the booker chose another zone, and none of its slots yet.
 *  A custom time stays: it is the same time on any calendar. */
export function forCalendar(w: When, calendarZone: string): When {
  return { ...w, zone: w.zone === w.calendarZone ? calendarZone : w.zone, calendarZone, slot: null };
}

/** Another zone: the same times, read on its clock. The day follows the slot picked, so the time
 *  picked does not move. */
export function withZone(w: When, zone: string): When {
  return { ...w, zone, day: w.slot ? localDay(w.slot, zone) : w.day };
}

export function withDay(w: When, day: string): When {
  return { ...w, day, slot: null };
}

export function withSlot(w: When, slot: string | null): When {
  return { ...w, slot };
}

const plus = (iso: string, minutes: number) => new Date(Date.parse(iso) + minutes * MINUTE).toISOString();

/** The next quarter hour from `now`. */
export function nextQuarter(now: number): string {
  return new Date(Math.ceil(now / (15 * MINUTE)) * 15 * MINUTE).toISOString();
}

/**
 * Default or custom. A custom time starts at the slot picked, else where it was, else the next
 * quarter hour, and lasts the calendar's duration until its end is changed. Back on the slots, the
 * day is the custom time's, and the slot stays picked if the custom time was left on it.
 */
export function withMode(w: When, mode: TimeMode, durationMinutes: number, now: number): When {
  if (mode === w.mode) return w;
  if (mode === "custom") {
    const from = w.slot ?? w.from ?? nextQuarter(now);
    const keep = !w.slot && w.to && w.from === from && Date.parse(w.to) > Date.parse(from);
    return { ...w, mode, from, to: keep ? w.to : plus(from, durationMinutes) };
  }
  const onSlot = w.slot !== null && w.from === w.slot;
  return { ...w, mode, slot: onSlot ? w.slot : null, day: w.from ? localDay(w.from, w.zone) : w.day };
}

/** The custom start typed, on `zone`'s clock. The end moves with it, keeping the meeting's length. */
export function withFrom(w: When, wall: string, durationMinutes: number): When {
  const from = instantOf(wall, w.zone);
  if (!from) return { ...w, from: null };
  const length = w.from && w.to ? Date.parse(w.to) - Date.parse(w.from) : 0;
  return { ...w, from, to: plus(from, length > 0 ? length / MINUTE : durationMinutes) };
}

/** The custom end typed, on `zone`'s clock. */
export function withTo(w: When, wall: string): When {
  return { ...w, to: instantOf(wall, w.zone) };
}

/** Why a custom time would be refused, by field, as core-api refuses it (ADR-169). */
export function customProblems(w: When, now: number): { from?: string; to?: string } {
  if (!w.from) return { from: "Pick when it starts" };
  if (!w.to) return { to: "Pick when it ends" };
  const from = Date.parse(w.from);
  const to = Date.parse(w.to);
  if (from <= now) return { from: "That time has passed" };
  if (to <= from) return { to: "Ends before it starts" };
  if (to - from > MAX_CUSTOM_MINUTES * MINUTE) return { to: "24 hours at most" };
  return {};
}

/** What is sent: the slot, or the custom start and end; null while there is none to send. */
export function chosenTime(w: When | null, now: number): { start: string; end?: string } | null {
  if (!w) return null;
  if (w.mode === "default") return w.slot ? { start: w.slot } : null;
  const problems = customProblems(w, now);
  return w.from && w.to && !problems.from && !problems.to ? { start: w.from, end: w.to } : null;
}

/** The other clocks a picked time is worth reading on: the calendar's and the contact's, when they
 *  are not the one it is shown in. */
export function otherClocks(w: When, contactZone?: string | null): { zone: string; whose: string }[] {
  const out: { zone: string; whose: string }[] = [];
  if (w.calendarZone !== w.zone) out.push({ zone: w.calendarZone, whose: "Calendar" });
  if (isZone(contactZone) && contactZone !== w.zone && contactZone !== w.calendarZone) {
    out.push({ zone: contactZone, whose: "Contact" });
  }
  return out;
}
